/**
 * Mirroring a change onto the other estimates on the same job.
 *
 * Pricing one roof three ways means keeping three estimates in step. Jonathon,
 * 2026-08-22: "whenever there are multiple replacement estimates and we edit,
 * remove or add line items it should prompt us if we want to make these same
 * changes to the rest of the roof replacement estimates for that customer …
 * that way we don't have to keep making the same changes on every estimate."
 *
 * What he asked for, in his own answers to the questions:
 *
 *   1. The other estimates on the *same job*, never another job's.
 *   2. Only while the customer has not signed — this is for building the
 *      estimate, not for maintaining a sold one.
 *   3. Quantities are the interesting case. Option A at 30 squares and option B
 *      at 34 are different on purpose, so a quantity that already differs is
 *      *asked* about rather than assumed.
 *   4. A price that already differs is left alone and reported, not overwritten.
 *   5. One prompt per visit to a section, covering everything changed in it.
 *   6. The estimate's own line items too, on the same terms.
 *
 * Nothing here talks to a database. The web app turns a {@link MirrorAction}
 * into inserts, updates and deletes, which is what makes the awkward part — the
 * matching and the conflicts — testable.
 */

import { jobIsWon } from "./estimates.js";
import { formatMoney, type Cents } from "./money.js";
import type { Estimate, Job } from "./types.js";

/** Which of an estimate's four editable buckets a change came from. */
export type MirrorBucket = "items" | "material" | "work" | "expenses";

export const MIRROR_BUCKET_LABEL: Record<MirrorBucket, string> = {
  items: "line items",
  material: "material order",
  work: "work order",
  expenses: "other expenses",
};

/**
 * A line, whatever table it came from.
 *
 * The four buckets spell the same four facts differently —
 * `estimate_items.customer_price` is a line total, a material item carries
 * `name` and `unit_price`, an expense carries `amount` and no quantity at all —
 * so the web app normalises before anything here sees a row. Money is cents,
 * as everywhere else.
 */
export interface MirrorRow {
  id: string;
  productId: string | null;
  label: string;
  qty: number;
  price: Cents;
  unit: string | null;
}

/** The four facts that can be mirrored. */
export type MirrorField = "label" | "qty" | "price" | "unit";

const FIELDS: readonly MirrorField[] = ["label", "qty", "price", "unit"];

const FIELD_LABEL: Record<MirrorField, string> = {
  label: "product",
  qty: "quantity",
  price: "price",
  unit: "unit",
};

/** One thing that happened while a section was open. */
export type MirrorChange =
  | { kind: "added"; key: string; row: MirrorRow }
  | { kind: "removed"; key: string; row: MirrorRow }
  | {
      kind: "changed";
      key: string;
      before: MirrorRow;
      after: MirrorRow;
      fields: readonly MirrorField[];
    };

/**
 * How a line is recognised on a different estimate.
 *
 * The product when there is one, because that survives a rename and is what
 * two estimates actually share. Failing that the name, lowercased and
 * collapsed, which is all a hand-typed line has.
 */
export function mirrorKey(row: Pick<MirrorRow, "productId" | "label">): string {
  if (row.productId) return `product:${row.productId}`;
  return `name:${row.label.trim().toLowerCase().replace(/\s+/g, " ")}`;
}

const sameValue = (a: unknown, b: unknown): boolean => {
  if (typeof a === "string" || typeof b === "string") {
    return String(a ?? "").trim() === String(b ?? "").trim();
  }
  return (a ?? null) === (b ?? null);
};

/**
 * What changed in one bucket between opening it and leaving it.
 *
 * Snapshot in, snapshot out — not a log of keystrokes. Type 34, think better of
 * it, type 30 again and nothing changed, which is the honest answer and the one
 * that keeps the prompt from appearing after a visit that did nothing. The same
 * goes for a line added and then removed, or an edit taken back with Undo.
 */
export function diffRows(
  before: readonly MirrorRow[],
  after: readonly MirrorRow[],
): MirrorChange[] {
  const was = new Map(before.map((row) => [row.id, row]));
  const changes: MirrorChange[] = [];

  for (const row of after) {
    const previous = was.get(row.id);
    if (!previous) {
      changes.push({ kind: "added", key: mirrorKey(row), row });
      continue;
    }
    was.delete(row.id);
    const fields = FIELDS.filter((f) => !sameValue(previous[f], row[f]));
    if (fields.length > 0) {
      changes.push({
        kind: "changed",
        // The key it had, so a line whose product was swapped is still found on
        // the other estimates by what it used to be.
        key: mirrorKey(previous),
        before: previous,
        after: row,
        fields,
      });
    }
  }

  for (const row of was.values()) {
    changes.push({ kind: "removed", key: mirrorKey(row), row });
  }

  return changes;
}

/** What a change should read as in the prompt. */
export function describeChange(change: MirrorChange): string {
  switch (change.kind) {
    case "added":
      return `Added ${change.row.label || "a line"}`;
    case "removed":
      return `Removed ${change.row.label || "a line"}`;
    case "changed": {
      const name = change.before.label || change.after.label || "a line";
      const parts = change.fields.map((field) => {
        if (field === "price") {
          return `${FIELD_LABEL[field]} ${formatMoney(change.before.price)} → ${formatMoney(change.after.price)}`;
        }
        const from = String(change.before[field] ?? "—") || "—";
        const to = String(change.after[field] ?? "—") || "—";
        return `${FIELD_LABEL[field]} ${from} → ${to}`;
      });
      return `${name}: ${parts.join(", ")}`;
    }
  }
}

/** The columns a mirrored insert or update would write. */
export type MirrorValues = Partial<Record<MirrorField, unknown>> & {
  productId?: string | null;
};

/** A quantity that already differs, which is the customer's to decide. */
export interface MirrorAsk {
  field: MirrorField;
  /** What this estimate has now. */
  theirs: number;
  /** What it would become. */
  mine: number;
}

/**
 * One change, aimed at one estimate.
 *
 * `operation` is what happens if the estimate is ticked. `ask` is the part that
 * needs a second tick — a quantity that was already different. `skipped` is
 * what was deliberately left alone, in words, because rule four is "skip and
 * tell me", and a silent skip is indistinguishable from a bug.
 */
export interface MirrorAction {
  change: MirrorChange;
  /** The row on the target estimate this acts on, if there is one. */
  targetRowId: string | null;
  operation: "insert" | "update" | "delete" | "none";
  values: MirrorValues;
  ask: MirrorAsk[];
  skipped: string[];
}

const valuesOf = (row: MirrorRow): MirrorValues => ({
  productId: row.productId,
  label: row.label,
  qty: row.qty,
  price: row.price,
  unit: row.unit,
});

/**
 * Work out what each change would do to one other estimate.
 *
 * Every change produces an action, including the ones that do nothing: a change
 * that cannot be mirrored still has to say so.
 */
export function planMirrorForTarget(
  changes: readonly MirrorChange[],
  targetRows: readonly MirrorRow[],
  /** How the estimate is referred to in the skip notes, e.g. "Option B". */
  targetName: string,
): MirrorAction[] {
  // Claimed as they are matched, so two lines carrying the same product on the
  // source estimate line up with two rows on the target rather than both
  // landing on the first one.
  const claimed = new Set<string>();
  const match = (key: string): MirrorRow | null =>
    targetRows.find((row) => mirrorKey(row) === key && !claimed.has(row.id)) ??
    null;

  return changes.map((change) => {
    const existing = match(change.key);
    if (existing) claimed.add(existing.id);

    if (change.kind === "added") {
      if (existing) {
        return {
          change,
          targetRowId: existing.id,
          operation: "none",
          values: {},
          ask: [],
          skipped: [`${targetName} already has ${existing.label || "it"}`],
        };
      }
      return {
        change,
        targetRowId: null,
        operation: "insert",
        values: valuesOf(change.row),
        ask: [],
        skipped: [],
      };
    }

    if (change.kind === "removed") {
      if (!existing) {
        return {
          change,
          targetRowId: null,
          operation: "none",
          values: {},
          ask: [],
          skipped: [
            `${change.row.label || "That line"} is not on ${targetName}`,
          ],
        };
      }
      return {
        change,
        targetRowId: existing.id,
        operation: "delete",
        values: {},
        ask: [],
        skipped: [],
      };
    }

    if (!existing) {
      return {
        change,
        targetRowId: null,
        operation: "none",
        values: {},
        ask: [],
        skipped: [
          `${change.before.label || "That line"} is not on ${targetName}`,
        ],
      };
    }

    const values: MirrorValues = {};
    const ask: MirrorAsk[] = [];
    const skipped: string[] = [];

    for (const field of change.fields) {
      // Already what it was about to become. Nothing to do, and nothing worth
      // telling anybody about.
      if (sameValue(existing[field], change.after[field])) continue;

      if (sameValue(existing[field], change.before[field])) {
        values[field] = change.after[field];
        // Swapping the product swaps what the line *is*, so the id goes with it.
        if (field === "label") values.productId = change.after.productId;
        continue;
      }

      // The two estimates already disagree here.
      if (field === "qty") {
        ask.push({
          field,
          theirs: existing.qty,
          mine: change.after.qty,
        });
        continue;
      }

      const theirs =
        field === "price"
          ? formatMoney(existing.price)
          : String(existing[field] ?? "—") || "—";
      skipped.push(
        `${targetName} has ${existing.label || "that line"} at ${FIELD_LABEL[field]} ${theirs} — left alone`,
      );
    }

    return {
      change,
      targetRowId: existing.id,
      operation: Object.keys(values).length > 0 ? "update" : "none",
      values,
      ask,
      skipped,
    };
  });
}

/** Does this action do anything at all, once the asks are settled? */
export function actionIsLive(action: MirrorAction): boolean {
  return action.operation !== "none" || action.ask.length > 0;
}

/** One of the other estimates on this job, and whether it may be mirrored to. */
export interface MirrorCandidate {
  estimate: Pick<Estimate, "id" | "name" | "version" | "status" | "trade">;
  /** What to call it on screen. */
  label: string;
  eligible: boolean;
  /** Why not, when it is not. */
  reason: string | null;
  /** Ticked when the prompt opens. */
  defaultOn: boolean;
}

/** What an estimate is called when it has no name. */
export const estimateLabel = (
  estimate: Pick<Estimate, "name" | "version">,
): string => estimate.name?.trim() || `Version ${estimate.version}`;

/**
 * The other estimates on this job, in the order they should be listed.
 *
 * A signed estimate is never offered — mirroring onto a contract the customer
 * has agreed to would rewrite what they bought. Neither is anything on a job
 * that has been sold: this exists for the hour spent building the options, and
 * `jobIsWon` is the line between that hour and everything after it.
 *
 * Ticked by default when the trade matches, because that is the case this was
 * asked for — three ways of pricing the same roof. A gutter option sitting
 * beside them is listed and left unticked rather than hidden, since the person
 * looking at the prompt knows better than the rule does.
 */
export function mirrorTargets({
  job,
  estimates,
  sourceId,
}: {
  job: Pick<Job, "stage" | "signed_contract_at" | "sends_at">;
  estimates: readonly Pick<
    Estimate,
    "id" | "name" | "version" | "status" | "trade"
  >[];
  sourceId: string;
}): MirrorCandidate[] {
  if (jobIsWon(job)) return [];

  const source = estimates.find((e) => e.id === sourceId);

  return estimates
    .filter((estimate) => estimate.id !== sourceId)
    .map((estimate) => {
      const signed = estimate.status === "approved";
      const sameTrade =
        (estimate.trade ?? "") === (source?.trade ?? "") &&
        (estimate.trade ?? "") !== "";
      return {
        estimate,
        label: estimateLabel(estimate),
        eligible: !signed,
        reason: signed ? "signed" : null,
        defaultOn: !signed && sameTrade,
      };
    });
}

/**
 * Is there anybody to mirror to, and anything to mirror?
 *
 * Both halves matter. A job with one estimate must never see the prompt, and
 * neither must a visit to a section that changed nothing — which is most
 * visits.
 */
export function mirrorOffered({
  changes,
  candidates,
}: {
  changes: readonly MirrorChange[];
  candidates: readonly MirrorCandidate[];
}): boolean {
  return changes.length > 0 && candidates.some((c) => c.eligible);
}
